import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { isEmpty } from 'lodash';
import {
  accordionSx,
  accordionDetailsSx,
  dividerSx,
  boldLabelSx,
  selectAllFormControlLabelSx,
  selectUserFormControlSx,
} from '../styles';
import { SelectedUser } from './PublishReportDialog';

export interface UserOptionItem {
  id: string;
  label: string | React.ReactElement;
  userName?: string;
  displayName?: string;
  roleName?: string;
  users?: string[];
}

interface UserOptions {
  id: string;
  label: string;
  options: UserOptionItem[];
}

interface UserSelectionAccordionProps {
  userGroup: UserOptions;
  index: number;
  selectedOptions: SelectedUser[];
  debouncedSearchText: string;
  isAllSelected: (groupId: string, allIds: string[]) => boolean;
  toggleSelectAll: (groupId: string, allIds: { id: string; name: string }[]) => void;
  toggleOption: (groupId: string, optionId: string, optionLabel: string, userName?: string) => void;
  getOptionName: (option: UserOptionItem) => string;
  mapOptionsForSelectAll: (options: UserOptionItem[]) => { id: string; name: string }[];
  selectAllLabel: string;
  showUserName?: boolean;
}

function UserSelectionAccordion({
  userGroup,
  index,
  selectedOptions,
  debouncedSearchText,
  isAllSelected,
  toggleSelectAll,
  toggleOption,
  getOptionName,
  mapOptionsForSelectAll,
  selectAllLabel,
  showUserName = false,
}: Readonly<UserSelectionAccordionProps>) {
  // Extract option IDs for reusability
  const optionIds = userGroup.options.map((o: UserOptionItem) => o.id);
  const mappedOptions = mapOptionsForSelectAll(userGroup.options);
  const showSelectAll = isEmpty(debouncedSearchText);

  // Helper to check if option is selected
  const isOptionChecked = (optionId: string) => selectedOptions.some((x) => x.optionId === optionId);

  return (
    <Box key={userGroup.id}>
      {index > 0 && <Divider sx={dividerSx} />}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionDetails sx={accordionDetailsSx}>
          <FormGroup>
            {showSelectAll && (
              <FormControlLabel
                key={`${userGroup.id}-select-all`}
                control={
                  <Switch
                    checked={isAllSelected(userGroup.id, optionIds)}
                    onChange={() => toggleSelectAll(userGroup.id, mappedOptions)}
                  />
                }
                sx={{ ...selectAllFormControlLabelSx(false), ...boldLabelSx }}
                labelPlacement='start'
                label={selectAllLabel}
              />
            )}
            {userGroup.options.map((option: UserOptionItem) => (
              <FormControlLabel
                key={option.id}
                control={
                  <Checkbox
                    checked={isOptionChecked(option.id)}
                    onChange={() =>
                      toggleOption(
                        userGroup.id,
                        option.id,
                        getOptionName(option),
                        showUserName ? option.userName : undefined
                      )
                    }
                  />
                }
                label={option.label}
                sx={showUserName ? selectUserFormControlSx : undefined}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default UserSelectionAccordion;
